import React from 'react';
import { connect, useSelector } from 'react-redux';
import { parse as parseQueryString, stringify as stringifyQueryString } from 'utils/queryString';
import { purchaseOnHoldTracker, purchaseTracker } from 'metrics/user-analytics/purchase';
import getCardBrand from 'utils/cards';
import { checkoutFunnelStep } from 'utils/Reserbus';
import { getPurchase, pollPurchaseComplete } from '@/actions/purchase';
import { confirmPaypalPayment } from '@/actions/paypal';
import { getInstance } from 'utils/taplytics';
import errorMonitoring from '../../../errorMonitoring';
import { setError } from '../../../actions';
import BasePurchaseComplete from './PurchaseComplete';
import PurchaseCompleteFlat from './flat/PurchaseComplete';
import { isHybridTrip } from '../../../utils/wayIsOpenTicket';

const PARSE_BOOLEANS = { parseBooleans: true };

/**
 * Fire purchase analytics events based on purchase state.
 * Errors are not caught here; the caller wraps this in try/catch.
 * @param {object} params - Analytics parameters.
 * @param {object} params.purchase - Purchase data.
 * @param {string} params.token - Purchase token.
 * @param {string} params.paymentEngine - Payment engine identifier.
 * @param {string} params.paymentType - Payment type.
 * @param {boolean} params.isExchange - Whether it is an exchange.
 * @param {number} params.passengersQuantity - Number of passengers.
 * @param {string} params.purchaseStatus - Current purchase status.
 * @param {boolean} params.taplyticsEnabled - Whether Taplytics tracking is on.
 */
function trackPurchaseAnalytics(params) {
  if (!params.purchase) {
    return;
  }

  const funnelStep = checkoutFunnelStep(params.purchase.departs, params.purchase.returns);
  const cardBrand = getCardBrand();
  purchaseTracker({
    purchase: params.purchase,
    funnelStep,
    cardBrand,
    paymentEngine: params.paymentEngine,
    paymentType: params.paymentType,
    isExchange: params.isExchange,
    passengersQuantity: params.passengersQuantity,
  });

  if (params.purchaseStatus !== 'completed') {
    purchaseOnHoldTracker(params.token);
  } else if (params.taplyticsEnabled) {
    const taplyticsInstance = getInstance();
    if (taplyticsInstance) {
      taplyticsInstance.track('purchase complete', { token: params.token });
    } else {
      errorMonitoring.notify({
        error: new Error('Taplytics enabled but instance not available'),
        info: { context: 'trackPurchaseAnalytics', token: params.token },
      });
    }
  }
}

const mapStateToProps = ({ purchase, payment, siemprePlus, whitelabelConfig }) => {
  const { user } = siemprePlus.toJS();
  const loaded = purchase.get('loaded');

  // Obtain number of passengers
  const passengers = purchase.get('passengers');
  const passengersQuantity = passengers ? passengers.size : 0;

  const jsPurchase = loaded && {
    email: purchase.get('email'),
    phone: purchase.get('phone'),
    phoneCode: purchase.get('phoneCode'),
    token: purchase.get('token'),
    total: purchase.get('total'),
    taxes: purchase.get('taxes'),
    markup: purchase.get('markup'),
    insuranceAmount: purchase.get('insuranceAmount'),
    passengerSelection: purchase.get('passengerSelection').toJS(),
    purchaserNationality: purchase.get('purchaserNationality'),
    departs: purchase.get('departs').toJS(),
    returns: purchase.get('returns').toJS(),
    roundTrip: purchase.get('roundTrip'),
    walletPointsUsed: purchase.get('walletTotalPointsUsed') || purchase.get('walletPointsUsed'),
    walletBalanceUsed: purchase.get('walletBalanceUsed'),
    walletType: purchase.get('walletType'),
    walletAccrualPoints: purchase.get('walletAccrualPoints'),
    isBulkTicket: purchase.get('bulkTicket'),
    isExpired: purchase.get('isExpired'),
    isDotersUser: purchase.get('isDotersUser'),
    isDotersPassenger: purchase.get('isDotersPassenger'),
  };

  return {
    loaded,
    isFetching: purchase.get('isFetching') || Boolean(payment.get('isFetching')),
    purchase: jsPurchase,
    purchaseStatus: purchase.get('state') || payment.get('purchaseState'),
    paymentStatus: payment.get('status'),
    paymentType: payment.get('paymentType') || 'credit_card',
    paymentEngine: payment.get('paymentEngine'),
    paymentProvider: payment.get('paymentProvider'),
    paymentId: payment.get('id'),
    transferPaymentState: payment.get('kushkiProcessorState'),
    isOpenTicket: Boolean(purchase.get('openTicket')),
    isExchange: Boolean(purchase.get('isExchange')),
    email: purchase.get('email'),
    departsTicketPdfUrl: purchase.getIn(['departs', 'fragments', 0, 'ticketPdfUrl']),
    departsTransporterKey: purchase.getIn(['departs', 'fragments', 0, 'transporterKey']),
    departsReserveCode: purchase.getIn(['departs', 'fragments', 0, 'reserveCode']) || null,
    returnTicketPdfUrl: purchase.getIn(['returns', 'fragments', 0, 'ticketPdfUrl']),
    returnsTransporterKey: purchase.getIn(['returns', 'fragments', 0, 'transporterKey']),
    departsTickets: purchase.getIn(['departs', 'fragments'])?.toJS()?.[0]?.tickets || [],
    returnsTickets: purchase.getIn(['returns', 'fragments'])?.toJS()?.[0]?.tickets || [],
    returnsReserveCode: purchase.getIn(['returns', 'fragments', 0, 'reserveCode']) || null,
    user,
    isHybridTrip: isHybridTrip(purchase.get('departs').toJS(), purchase.get('returns').toJS()),
    passengersQuantity,
    taplyticsEnabled: Boolean(whitelabelConfig?.env?.taplytics?.enabled),
  };
};

const mapDispatchToProps = {
  getPurchase,
  pollPurchaseComplete,
  setError,
  confirmPaypalPayment,
};

/**
 * Whether the purchase redeemed Doters loyalty points.
 * Drives the `usedPoints` URL flag that Hotjar reads to segment the Doters
 * points-redemption survey (MSI-1423) without embedding survey code in the funnel.
 * @param {object} purchase - Serialized purchase from mapStateToProps.
 * @returns {boolean} True when the wallet is Doters and points/balance were applied.
 */
function purchaseRedeemedDotersPoints(purchase) {
  if (!purchase) return false;
  const { walletType, walletBalanceUsed, walletPointsUsed } = purchase;
  // Evaluate each field independently: a `||` fallback would pick a negative
  // walletBalanceUsed over a positive walletPointsUsed and drop the flag.
  return walletType === 'doters' && (Number(walletBalanceUsed) > 0 || Number(walletPointsUsed) > 0);
}

/**
 * Build the cleaned URL after removing the trackPurchase param.
 * @param {string} search - The location.search string.
 * @param {string} token - Purchase token.
 * @param {boolean} [usedPoints=false] - Append `usedPoints=true` for Hotjar survey targeting.
 * @returns {string} Cleaned purchase-complete URL.
 */
function buildCleanUrl(search, token, usedPoints = false) {
  const parsed = parseQueryString(search);
  delete parsed.trackPurchase;
  // `usedPoints` is a managed flag: always strip any incoming (possibly stale)
  // value, then re-add it only for the current points-redemption purchase.
  delete parsed.usedPoints;
  if (usedPoints) {
    parsed.usedPoints = true;
  }
  const qs = stringifyQueryString(parsed);
  return `/purchase/${token}/complete${qs ? `?${qs}` : ''}`;
}

/**
 * Read trackPurchase query flag and purchase token from router props.
 * @param {object} ownProps - location, match from react-router.
 * @returns {{ trackPurchase: boolean, token: string }} Fields consumed by mergeProps.
 */
function getPurchaseCompleteRouting(ownProps) {
  return {
    trackPurchase:
      parseQueryString(ownProps.location.search, PARSE_BOOLEANS).trackPurchase ?? false,
    token: ownProps.match.params.token,
  };
}

/**
 * Run purchase analytics, report failures, then clean the URL when purchase data exists.
 * @param {object} stateProps - Redux slice props for analytics and URL guard.
 * @param {object} ownProps - Router props (history, location).
 * @param {string} token - Purchase route token.
 */
function invokeTrackPurchaseHandler(stateProps, ownProps, token) {
  try {
    trackPurchaseAnalytics({
      purchase: stateProps.purchase,
      token,
      paymentEngine: stateProps.paymentEngine,
      paymentType: stateProps.paymentType,
      isExchange: stateProps.isExchange,
      passengersQuantity: stateProps.passengersQuantity,
      purchaseStatus: stateProps.purchaseStatus,
      taplyticsEnabled: stateProps.taplyticsEnabled,
    });
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: {
        context: 'trackPurchaseHandler',
        token,
        loaded: stateProps.loaded,
        hasPurchase: Boolean(stateProps.purchase),
      },
    });
  } finally {
    // Do not strip trackPurchase from the URL until purchase data exists, or analytics can never run on retry.
    if (stateProps.purchase) {
      const usedPoints = purchaseRedeemedDotersPoints(stateProps.purchase);
      try {
        ownProps.history.replace(buildCleanUrl(ownProps.location.search, token, usedPoints));
      } catch (urlError) {
        errorMonitoring.notify({
          error: urlError,
          info: { context: 'trackPurchaseHandler:urlCleanup', token },
        });
        ownProps.history.replace(
          `/purchase/${token}/complete${usedPoints ? '?usedPoints=true' : ''}`,
        );
      }
    }
  }
}

/**
 * Merge Redux and router props, injecting trackPurchaseHandler that runs analytics and cleans the URL.
 * @param {object} stateProps - Redux state props.
 * @param {object} dispatchProps - Redux dispatch props.
 * @param {object} ownProps - Component own props from router.
 * @returns {object} Merged props including token, trackPurchase flag, and trackPurchaseHandler.
 */
export function mergeProps(stateProps, dispatchProps, ownProps) {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    ...getPurchaseCompleteRouting(ownProps),
    trackPurchaseHandler: () =>
      invokeTrackPurchaseHandler(stateProps, ownProps, getPurchaseCompleteRouting(ownProps).token),
  };
}

/**
 * PurchaseComplete component.
 * @param {object} props - Routed purchase complete props from mergeProps.
 */
const PurchaseComplete = (props) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const FLAT_STYLE = features.FUNNEL_STYLE === 'FLAT';
  const PurchaseCompleteToRender = FLAT_STYLE ? PurchaseCompleteFlat : BasePurchaseComplete;

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <PurchaseCompleteToRender {...props} />;
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PurchaseComplete);
